/**
 * SMS licensing service over the typed `@tiaki/http-client` per ADR 113.
 *
 * Two endpoints fall through to `client.ky` (not in placeholder schema):
 *   - /api/v2/licensing/licenses/{id}/seats/
 *
 * `/sites/{site_id}/modules/` IS in the schema as
 * `/api/v2/licensing/licenses/{id}/sites/{site_id}/modules/`.
 */

import { client } from '../lib/http-client';

function unwrap<T>(result: { data?: T; error?: unknown }): T {
  if (result.error !== undefined) {
    throw result.error;
  }
  return result.data as T;
}

// ── Types ────────────────────────────────────────────────────────────────────

export type LicenseStatus =
  | 'trial'
  | 'active'
  | 'grace'
  | 'expired'
  | 'suspended'
  | 'revoked'
  | 'purged';

export type LicenseType = 'standard' | 'enterprise' | 'trial' | 'partner';
export type LicenseChannel = 'direct' | 'reseller' | 'oem';
export type LicensingModel = 'site' | 'user' | 'concurrent' | 'unlimited';

export interface LicenseModule {
  module: number;
  module_code: string;
  module_name: string;
  enabled: boolean;
}

export interface License {
  id: number;
  organization: number;
  organization_name: string;
  product: number;
  product_code: string;
  product_name: string;
  vendor: number | null;
  license_key: string;
  type: LicenseType;
  channel: LicenseChannel;
  status: LicenseStatus;
  max_seats: number | null;
  starts_at: string;
  expires_at: string | null;
  grace_period_days: number;
  trial_days: number;
  auto_renew: boolean;
  zoho_subscription_id: string | null;
  metadata: Record<string, unknown>;
  legal_hold: boolean;
  license_modules: LicenseModule[];
  is_usable: boolean;
  is_degraded: boolean;
  created_at: string;
  updated_at: string;
}

export interface Product {
  id: number;
  name: string;
  code: string;
  description: string;
  licensing_model: LicensingModel;
  status: string;
}

export interface Module {
  id: number;
  product: number;
  name: string;
  code: string;
  description: string;
  is_base: boolean;
}

export interface SiteModuleOverride {
  id: number;
  site: number;
  site_name: string;
  module: number;
  module_code: string;
  enabled: boolean;
}

export interface SeatAssignment {
  id: number;
  license: number;
  user: number;
  assigned_at: string;
  released_at: string | null;
}

/**
 * `EntitlementStatus` widens `LicenseStatus` with `'unlicensed'`, which the
 * backend's `/entitlements/` endpoint returns when no License record exists
 * for the org (the per-org gate fallback: no License = full access). See
 * ADR 104 §3.3.
 */
export type EntitlementStatus = LicenseStatus | 'unlicensed';

export interface Entitlements {
  status: EntitlementStatus;
  product: string;
  expires_at: string | null;
  grace_period_days: number;
  is_degraded: boolean;
  modules: Record<string, boolean>;
}

export interface LicenseListResponse {
  count: number;
  next: string | null;
  previous: string | null;
  results: License[];
}

export interface LicenseFilters {
  status?: LicenseStatus;
  page?: number;
  page_size?: number;
  [key: string]: unknown;
}

// ── API Functions ────────────────────────────────────────────────────────────

export async function getEntitlements(): Promise<Entitlements> {
  const result = await client.GET('/api/v2/licensing/entitlements/', {});
  return unwrap(result) as unknown as Entitlements;
}

export async function getLicenses(
  filters: LicenseFilters = {},
): Promise<LicenseListResponse> {
  const result = await client.GET('/api/v2/licensing/licenses/', {
    params: { query: filters as never },
  });
  return unwrap(result) as unknown as LicenseListResponse;
}

export async function getLicenseById(id: number): Promise<License> {
  const result = await client.GET('/api/v2/licensing/licenses/{id}/', {
    params: { path: { id } as never },
  });
  return unwrap(result) as unknown as License;
}

export async function getProducts(): Promise<Product[]> {
  const result = await client.GET('/api/v2/licensing/products/', {});
  const data = unwrap(result) as unknown as { results: Product[] };
  return data.results;
}

export async function getModules(): Promise<Module[]> {
  const result = await client.GET('/api/v2/licensing/modules/', {});
  const data = unwrap(result) as unknown as { results: Module[] };
  return data.results;
}

export async function getSiteModuleOverrides(
  licenseId: number,
  siteId: number,
): Promise<SiteModuleOverride[]> {
  const result = await client.GET(
    '/api/v2/licensing/licenses/{id}/sites/{site_id}/modules/',
    {
      params: { path: { id: licenseId, site_id: siteId } as never },
    },
  );
  const data = unwrap(result) as unknown as { results: SiteModuleOverride[] };
  return data.results;
}

export async function getSeatAssignments(
  licenseId: number,
): Promise<SeatAssignment[]> {
  // /api/v2/licensing/licenses/{id}/seats/ not in placeholder schema; ky fallthrough.
  const response = await client.ky.get(
    `api/v2/licensing/licenses/${licenseId}/seats/`,
  );
  const data = (await response.json()) as { results: SeatAssignment[] };
  return data.results;
}

// ── License Templates (ADR 104 §18) ──────────────────────────────────────────
// The /templates/ and /from-template/ endpoints + the lifecycle actions
// are not in the openapi placeholder schema, so we route through
// client.ky like getSeatAssignments above.

export interface LicenseTemplateModule {
  module: number;
  module_code: string;
  module_name: string;
  enabled_by_default: boolean;
}

export interface LicenseTemplate {
  id: string; // UUID
  name: string;
  vendor: number | null;
  vendor_name: string | null;
  product: number;
  product_code: string;
  product_name: string;
  default_type: 'site' | 'user';
  default_channel: LicenseChannel;
  default_trial_days: number;
  default_grace_period_days: number;
  default_max_seats: number | null;
  notes: string;
  is_active: boolean;
  template_modules: LicenseTemplateModule[];
  created_at: string;
  updated_at: string;
}

export interface LicenseTemplateWritePayload {
  name: string;
  vendor?: number | null;
  product: number;
  default_type?: 'site' | 'user';
  default_channel?: LicenseChannel;
  default_trial_days?: number;
  default_grace_period_days?: number;
  default_max_seats?: number | null;
  notes?: string;
  is_active?: boolean;
  module_codes?: string[];
}

export interface LicenseFromTemplatePayload {
  organization_id: number;
  template_id: string;
  overrides?: Record<string, unknown>;
}

export type LicenseStatusAction = 'activate' | 'suspend' | 'revoke' | 'renew';

export async function getLicenseTemplates(): Promise<LicenseTemplate[]> {
  const response = await client.ky.get('api/v2/licensing/templates/');
  const data = (await response.json()) as
    | { results: LicenseTemplate[] }
    | LicenseTemplate[];
  return Array.isArray(data) ? data : data.results;
}

export async function createLicenseTemplate(
  payload: LicenseTemplateWritePayload,
): Promise<LicenseTemplate> {
  const response = await client.ky.post('api/v2/licensing/templates/', {
    json: payload,
  });
  return (await response.json()) as LicenseTemplate;
}

export async function updateLicenseTemplate(
  id: string,
  payload: Partial<LicenseTemplateWritePayload>,
): Promise<LicenseTemplate> {
  const response = await client.ky.patch(
    `api/v2/licensing/templates/${id}/`,
    { json: payload },
  );
  return (await response.json()) as LicenseTemplate;
}

export async function deleteLicenseTemplate(id: string): Promise<void> {
  await client.ky.delete(`api/v2/licensing/templates/${id}/`);
}

export async function setLicenseTemplateModule(
  templateId: string,
  module_code: string,
  enabled_by_default: boolean,
): Promise<LicenseTemplateModule> {
  const response = await client.ky.post(
    `api/v2/licensing/templates/${templateId}/modules/`,
    { json: { module_code, enabled_by_default } },
  );
  return (await response.json()) as LicenseTemplateModule;
}

export async function createLicenseFromTemplate(
  payload: LicenseFromTemplatePayload,
): Promise<License> {
  const response = await client.ky.post(
    'api/v2/licensing/licenses/from-template/',
    { json: payload },
  );
  return (await response.json()) as License;
}

export async function setLicenseStatusAction(
  licenseId: number,
  action: LicenseStatusAction,
  body: Record<string, unknown> = {},
): Promise<License> {
  const response = await client.ky.post(
    `api/v2/licensing/licenses/${licenseId}/${action}/`,
    { json: body },
  );
  return (await response.json()) as License;
}

export async function setLicenseModule(
  licenseId: number,
  module_code: string,
  enabled: boolean,
): Promise<LicenseModule> {
  const response = await client.ky.post(
    `api/v2/licensing/licenses/${licenseId}/modules/`,
    { json: { module_code, enabled } },
  );
  return (await response.json()) as LicenseModule;
}

export async function setSiteModuleOverride(
  licenseId: number,
  siteId: number,
  module_code: string,
  enabled: boolean,
): Promise<SiteModuleOverride> {
  const response = await client.ky.post(
    `api/v2/licensing/licenses/${licenseId}/sites/${siteId}/modules/`,
    { json: { module_code, enabled } },
  );
  return (await response.json()) as SiteModuleOverride;
}
